import { asLocale, logout, selectCurrentUser, selectIsAuthenticated, selectIsSessionUnresolved, selectMemberships, selectPermissions, selectRole, setUserLocale, useUserPhotoObjectUrl } from '@saniplus/api-client';

import { FALLBACK_TIME_ZONE } from '../features/shared/timeZone';
import { useUiLocale } from './shared/useUiLocale';
import {
  AppShellAdmin,
  useDocumentVisible,
  type AccountScope,
  type AdminNavGroup,
  type AdminNavItem,
} from '@saniplus/ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router';

import { useAppDispatch, useAppSelector } from '../app/hooks';
import { useGetExceptionSummaryQuery } from '../features/exceptions/api/exceptionsApi';
import { useListShiftsQuery } from '../features/horaires/api/horairesApi';
import { addDays, todayIn } from '../features/horaires/shared/time';
import {
  NO_PORTAL_ACCESS_PATH,
  currentHref,
  landingPath,
  visibleGroups,
  type NavItemDef,
} from './nav';
import { formatName } from '../features/shared/names';

const BADGE_POLL_MS = 30_000;
const BADGE_CAP = 99;

/**
 * The horizon the *Quarts ouverts* badge counts over — the same fourteen nights
 * A4 itself opens on, so the number in the rail and the number of rows on the
 * page are the same number. A badge counting a different window than the screen
 * it points at is worse than no badge.
 */
const OPEN_SHIFT_HORIZON_NIGHTS = 14;

/** The zone the horizon is counted in when no single site is in scope. */
const HORIZON_ZONE = FALLBACK_TIME_ZONE;

/**
 * The authenticated shell.
 *
 * `AppShellAdmin` owns the chassis, the three structures (216 px sidebar ≥ 1280,
 * 80 px labelled rail 769–1279, 280 px drawer ≤ 768) and the roving-tabindex
 * navigation. This file owns everything that is *this app's*: which items the
 * caller may see, which one is current, what the badge counts, and what the
 * account menu does.
 *
 * `design/pages/admin/A1-dashboard/JUDGEMENT.md` §5 is the binding spec for all
 * of it, including the two rules that are easy to get silently wrong: exactly
 * one `aria-current` in the whole tree, and an item the caller lacks permission
 * for is **absent**, never disabled.
 */
export function AppLayout() {
  const { t } = useTranslation(['nav', 'common']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  const user = useAppSelector(selectCurrentUser);
  const role = useAppSelector(selectRole);
  const memberships = useAppSelector(selectMemberships);
  const permissions = useAppSelector(selectPermissions);
  const authenticated = useAppSelector(selectIsAuthenticated);
  const unresolved = useAppSelector(selectIsSessionUnresolved);
  const visible = useDocumentVisible();

  /*
   * The shell's own account tile (Phase 6, P-10): `null` short-circuits with
   * no fetch for the common case of a caller with no photo yet, and the same
   * module-level cache the roster's avatars share means a supervisor who just
   * replaced their photo on `/profil` sees the shell pick it up too, rather
   * than two independent copies of "the caller's photo" drifting apart.
   */
  const photo = useUserPhotoObjectUrl('me', user?.photoUpdatedAt ?? null);

  /*
   * The scope the supervisor is thinking in. It lives here rather than in the
   * store because nothing consumes it yet: the three dashboard endpoints take
   * no `contractId`, and every list screen owns its own contract filter in the
   * URL. Lifting it to a slice is a one-file change the day a screen wants it.
   */
  const [scopeKey, setScopeKey] = useState<string>('all');

  /*
   * §5.4: the badge is the count of OPEN, HIGH-severity exceptions. It renders
   * only when `exception:read` is held and the count is above zero — it never
   * renders `0`, and it stays suppressed while `/exceptions/summary` is still
   * loading rather than showing a stale number beside a fresh screen.
   */
  const canReadExceptions = permissions.includes('exception:read');
  const summary = useGetExceptionSummaryQuery(
    { status: 'open', severity: 'high' },
    {
      skip: !canReadExceptions || !authenticated,
      pollingInterval: visible ? BADGE_POLL_MS : 0,
      // Seconds, not `true`: the badge is polled, and a remount (a route
      // change unmounting and remounting the shell in a future layout) must
      // not turn every navigation into a second read of the same summary.
      refetchOnMountOrArgChange: 30,
    },
  );
  const highOpen = summary.data?.total ?? 0;

  /*
   * The second badge: unfilled shifts inside the same fourteen-night horizon A4
   * opens on. Same rules as the exceptions badge — held permission, never a
   * zero, never a number carried over from a load that has not landed — and the
   * same 30 s cadence, so a supervisor watching the rail sees the two counts
   * move together rather than one lagging the other by half a minute.
   *
   * `pageSize: 1` on purpose: the badge wants `total`, not the rows. The server
   * caps `GET /shifts` at one page (REVIEW-frontend, backend hand-off), and
   * asking for the smallest page keeps this read off the page-cap question
   * entirely — `total` is the whole query's count either way.
   */
  const canReadShifts = permissions.includes('shift:read');
  const openFrom = todayIn(HORIZON_ZONE);
  const openShifts = useListShiftsQuery(
    {
      status: 'open',
      from: openFrom,
      to: addDays(openFrom, OPEN_SHIFT_HORIZON_NIGHTS - 1, HORIZON_ZONE),
      pageSize: 1,
    },
    {
      skip: !canReadShifts || !authenticated,
      pollingInterval: visible ? BADGE_POLL_MS : 0,
      refetchOnMountOrArgChange: 30,
    },
  );
  const openShiftCount = openShifts.data?.total ?? 0;

  const active = useMemo(() => currentHref(location.pathname), [location.pathname]);

  const groups = useMemo<AdminNavGroup[]>(() => {
    const toItem = (item: NavItemDef): AdminNavItem => {
      /*
       * The two nav counts are not the same kind of fact, and since
       * 2026-08-31 they no longer look it. Open high-severity exceptions are
       * the one count in the product that means *act now*, so they keep the
       * danger field — the whole product's remaining red. Unfilled shifts
       * inside the fourteen-night horizon are a planning gap a supervisor
       * works through, not an alarm, so they take the `Badge`'s own default.
       */
      const badge =
        item.key === 'exceptions' && highOpen > 0
          ? {
              count: Math.min(highOpen, BADGE_CAP),
              label: t('nav:badge.exceptions', { count: highOpen }),
              tone: 'danger' as const,
            }
          : item.key === 'horaires-ouverts' && openShiftCount > 0
            ? {
                count: Math.min(openShiftCount, BADGE_CAP),
                label: t('nav:badge.openShifts', { count: openShiftCount }),
              }
            : undefined;

      return {
        key: item.key,
        label: t(`nav:${item.labelKey}`),
        ...(item.href === undefined ? {} : { href: item.href }),
        ...(item.icon === undefined ? {} : { icon: item.icon }),
        ...(item.href !== undefined && item.href === active ? { current: true } : {}),
        ...(badge === undefined ? {} : { badge }),
        ...(item.children === undefined ? {} : { children: item.children.map(toItem) }),
      };
    };

    return visibleGroups(permissions).map((group) => ({
      key: group.key,
      ...(group.separated === true ? { separated: true } : {}),
      items: group.items.map(toItem),
    }));
  }, [permissions, active, highOpen, openShiftCount, t]);

  const name = user === null ? '' : formatName(user);

  /** One row per contract the caller holds a membership on, plus "all". */
  const scopes = useMemo<AccountScope[]>(() => {
    const contracts = memberships.map<AccountScope>((membership) => ({
      key: membership.contractId,
      label: membership.contractName,
      ...(scopeKey === membership.contractId ? { current: true } : {}),
    }));
    if (memberships.length <= 1) return contracts;
    return [
      {
        key: 'all',
        label: t('nav:account.allContracts'),
        ...(scopeKey === 'all' ? { current: true } : {}),
      },
      ...contracts,
    ];
  }, [memberships, scopeKey, t]);

  /** "Superviseur · Olymel · 3 sites" — already assembled, as the shell expects. */
  const scopeLine = useMemo(() => {
    /*
     * A platform admin's scope is every contract (Phase 4: `user.role`, one
     * system-wide value; their memberships, if any, are ignored). So the line is
     * the scope alone, not the role: « Administrateur de plateforme · Toutes
     * les portées » truncates to "ADMINISTRATEU…" in a 216 px sidebar, and of
     * the two halves the scope is the one a reader cannot infer — the role is
     * already legible from the two elevated nav items nobody else sees.
     */
    if (role === 'platform_admin') return t('nav:account.platformScope');
    const first = memberships[0];
    // No scope at all (fail closed): nothing to describe under the name.
    if (first === undefined || role === null) return '';
    // `account.roles` names every `RoleSchema` member, the reserved `team_lead`
    // included (`AppLayout.spec.tsx` proves it), so no raw enum can leak here.
    const roleWord = t(`nav:account.roles.${role}`);
    const contract =
      memberships.length > 1
        ? t('nav:account.contracts', { count: memberships.length })
        : first.contractName;
    const sites =
      first.siteIds === null
        ? t('nav:account.allSites')
        : t('nav:account.siteCount', { count: first.siteIds.length });
    return t('nav:account.scopeLine', { role: roleWord, contract, sites });
  }, [memberships, role, t]);

  const { locale } = useUiLocale();

  /*
   * Q6 (Phase 4): a session that can see NOTHING in this shell — a worker with
   * `/dashboard` bookmarked, or a stale tab — is sent to the no-portal-access
   * screen before the chassis is drawn. `IndexRedirect` only covers `/`; a deep
   * link would otherwise render nine empty groups over a refused page, which
   * is the state that screen exists to replace. Never while the session is
   * still unresolved: `permissions` is `[]` then for the wrong reason, and the
   * guard above already holds the route until `/auth/me` answers.
   */
  if (!unresolved && landingPath(permissions) === NO_PORTAL_ACCESS_PATH) {
    return <Navigate to={NO_PORTAL_ACCESS_PATH} replace />;
  }

  return (
    <AppShellAdmin
      brandLabel={t('nav:brand')}
      brandSubLabel={t('nav:brandSub')}
      {...(active === '/dashboard' ? {} : { brandHref: '/dashboard' })}
      groups={groups}
      navLabel={t('nav:navLabel')}
      menuLabel={t('nav:menu')}
      closeMenuLabel={t('nav:closeMenu')}
      skipLabel={t('nav:skip')}
      groupToggleLabel={(label) => t('nav:groupToggle', { label })}
      /*
       * The shell hands its own `onNavigate` to the link (ui follow-up): in
       * drawer mode it closes the 280 px panel that would otherwise stay open
       * over the page it just opened, and at every other width it is a no-op.
       *
       * This replaces remounting the shell on `location.pathname`. That
       * workaround also remounted the whole route subtree, so a rotation or a
       * resize that crossed 768 px threw away every uncommitted form on screen
       * — a supervisor typing a ten-character reason lost it by turning the
       * tablet. The nav's scroll position and its open groups survive now too.
       */
      renderNavLink={(item, className, children, { onNavigate, tabIndex }) => (
        <Link
          to={item.href ?? '/dashboard'}
          data-nav-focusable=""
          // Comes from the shell, never re-derived from `item.current`: on a
          // route outside NAV_GROUPS (/profil, any 404) nothing is current, and
          // deriving here would leave the whole nav unreachable by keyboard.
          tabIndex={tabIndex}
          aria-current={item.current === true ? 'page' : undefined}
          className={className}
          onClick={onNavigate}
        >
          {children}
        </Link>
      )}
      account={{
        name,
        ...(photo.status === 'ready' ? { photoSrc: photo.url } : {}),
        // Client-side, like `onChangePin` below: a raw href would reload the
        // document and drop the in-memory access token and the query cache.
        onSelectProfile: () => void navigate('/profil'),
        profileLabel: t('nav:account.profile'),
        scopeLine,
        triggerLabel: t('nav:account.trigger', { name }),
        menuLabel: t('nav:account.menu'),
        scopeSectionLabel: t('nav:account.scopeSection'),
        scopes,
        onScopeChange: setScopeKey,
        languageSectionLabel: t('nav:account.languageSection'),
        locale,
        localeOptions: [
          { value: 'fr', label: t('common:language.fr') },
          { value: 'en', label: t('common:language.en') },
        ],
        onLocaleChange: (next) => {
          const chosen = asLocale(next) ?? 'fr';
          void i18n.changeLanguage(chosen);
          void dispatch(setUserLocale(chosen));
        },
        changePinLabel: t('nav:account.changePin'),
        onChangePin: () => void navigate('/pin'),
        signOutLabel: t('nav:account.signOut'),
        onSignOut: () => void dispatch(logout()),
      }}
    >
      <Outlet />
    </AppShellAdmin>
  );
}
